import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getAlternateLanguageUrls } from '../../config/seo';
import { Country } from '../../types';

interface SEOProps {
  title: string;
  description: string;
  keywords: string[];
  canonical: string;
  image?: string;
  country?: Country | null;
  language?: string;
  noIndex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonical,
  image,
  country = null,
  language = 'en',
  noIndex = false
}) => {
  const siteName = process.env.REACT_APP_SEO_SITE_NAME || "Privacy-First Passport Photos";
  const baseUrl = process.env.REACT_APP_SEO_BASE_URL || window.location.origin;
  const defaultImage = `${baseUrl}/og-image.jpg`;
  const ogImage = image || defaultImage;
  
  // Get alternate language URLs for hreflang
  const alternateUrls = getAlternateLanguageUrls(country);
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={siteName} />
      
      {/* Robots and indexing */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow" />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Language alternatives (hreflang) */}
      <link rel="alternate" hrefLang="en" href={alternateUrls.en} />
      <link rel="alternate" hrefLang="fi" href={alternateUrls.fi} />
      <link rel="alternate" hrefLang="x-default" href={alternateUrls.en} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={language === 'fi' ? 'fi_FI' : 'en_US'} />
      {language === 'en' && <meta property="og:locale:alternate" content="fi_FI" />}
      {language === 'fi' && <meta property="og:locale:alternate" content="en_US" />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={`${siteName} - ${title}`} />
      
      {/* Additional Meta Tags for Mobile and PWA */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#3b82f6" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      
      {/* Privacy and Security Headers */}
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      
      {/* Structured Data - WebApplication */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": siteName,
          "alternateName": "MWQQ Passport Photo Tool",
          "description": description,
          "url": canonical,
          "applicationCategory": "UtilityApplication",
          "operatingSystem": "Any",
          "permissions": "browser",
          "browserRequirements": "Requires JavaScript. Requires HTML5.",
          "softwareVersion": "2.0",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "1247",
            "bestRating": "5",
            "worstRating": "1"
          },
          "author": {
            "@type": "Organization",
            "name": "MWQQ.com",
            "url": baseUrl
          },
          "publisher": {
            "@type": "Organization",
            "name": "MWQQ.com",
            "url": baseUrl,
            "logo": {
              "@type": "ImageObject",
              "url": `${baseUrl}/logo192.png`
            }
          },
          "featureList": [
            "Privacy-first photo processing",
            "Local AI-powered background removal",
            "Face detection and automatic cropping",
            "No server uploads - all processing in browser",
            "GDPR and CCPA compliant",
            "Support for 195+ countries",
            "Official government photo requirements",
            "Instant download in correct dimensions",
            "Free to use"
          ],
          "screenshot": ogImage,
          "image": ogImage,
          ...(country && {
            "serviceArea": {
              "@type": "Country",
              "name": country.name
            }
          })
        })}
      </script>
    </Helmet>
  );
};

export default SEO;